import { useState } from 'react';
import {
  demoRequestHref,
  formatPricingMessages,
  formatPricingUsd,
  pricing,
  pricingAnnualMonthlyUsd,
  PRICING_ANNUAL_DISCOUNT_PERCENT,
  type PricingPlan,
} from '@/content/site';
import { ScrollReveal } from '@/components/ScrollReveal';
import { Section } from '@/components/Section';
import { trackWhatsAppSalesClick } from '@/lib/tracking';
import './Pricing.css';

type BillingCycle = 'monthly' | 'annual';

function CheckIcon() {
  return (
    <svg className="pricing-feature__icon" viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M13.2 4.2a.75.75 0 0 1 0 1.06l-5.5 5.5a.75.75 0 0 1-1.06 0l-2.5-2.5a.75.75 0 1 1 1.06-1.06L7 9.09l4.97-4.97a.75.75 0 0 1 1.23.1Z"
        fill="currentColor"
      />
    </svg>
  );
}

function messagesHeadline(plan: PricingPlan): string {
  if (plan.messagesPerMonth != null) {
    return `Hasta ${formatPricingMessages(plan.messagesPerMonth)} mensajes por mes`;
  }
  return plan.messagesLabel ?? 'Mensajes a medida';
}

function PricingCard({
  plan,
  isAnnual,
}: {
  plan: PricingPlan;
  isAnnual: boolean;
}) {
  const monthlyPrice = plan.monthlyPriceUsd;
  const annualMonthlyPrice = pricingAnnualMonthlyUsd(monthlyPrice);
  const displayPrice = isAnnual ? annualMonthlyPrice : monthlyPrice;

  return (
    <article
      className={`pricing-card pricing-card--${plan.id}${plan.highlighted ? ' pricing-card--highlighted' : ''}`}
    >
      {plan.bannerLabel ? (
        <div className="pricing-card__banner">{plan.bannerLabel}</div>
      ) : null}
      <header className="pricing-card__header">
        <div className="pricing-card__title-row">
          <h3 className="pricing-card__name">{plan.name}</h3>
          {plan.badge ? <span className="pricing-card__badge">{plan.badge}</span> : null}
        </div>
        <p className="pricing-card__tagline">{plan.tagline}</p>
      </header>

      <div className="pricing-card__price-block">
        <div className="pricing-card__price-row">
          <span className="pricing-card__price">{formatPricingUsd(displayPrice)}</span>
          {isAnnual ? (
            <span className="pricing-card__price-compare" aria-hidden="true">
              {formatPricingUsd(monthlyPrice)}
            </span>
          ) : null}
        </div>
        <span className="pricing-card__period">/mes</span>
        {isAnnual ? (
          <p className="pricing-card__annual-note">
            Facturado anualmente · ahorras{' '}
            <strong>{formatPricingUsd(monthlyPrice - annualMonthlyPrice)}/mes</strong>
          </p>
        ) : null}
      </div>

      <a
        href={demoRequestHref}
        className={`pricing-card__cta${plan.highlighted ? ' pricing-card__cta--primary' : ''}`}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackWhatsAppSalesClick('pricing', plan.cta, demoRequestHref)}
      >
        {plan.cta}
      </a>

      {plan.whyIdeal?.length ? (
        <div className="pricing-card__why">
          <p>
            <strong>Ideal si:</strong>
          </p>
          <ul>
            {plan.whyIdeal.map((item) => (
              <li key={item}>✓ {item}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <ul className="pricing-card__features">
        <li className="pricing-feature pricing-feature--primary">
          <CheckIcon />
          <div className="pricing-feature__body">
            <strong>{messagesHeadline(plan)}</strong>
          </div>
        </li>
        {plan.features.map((feature) => (
          <li key={feature.title} className="pricing-feature">
            <CheckIcon />
            <div className="pricing-feature__body">
              <span className="pricing-feature__title">{feature.title}</span>
              {feature.detail ? (
                <span className="pricing-feature__detail">{feature.detail}</span>
              ) : null}
              {feature.items?.length ? (
                <ul className="pricing-feature__sublist">
                  {feature.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : null}
            </div>
          </li>
        ))}
      </ul>
    </article>
  );
}

export function Pricing() {
  const [billing, setBilling] = useState<BillingCycle>('annual');
  const isAnnual = billing === 'annual';

  return (
    <Section
      id="precios"
      label={pricing.label}
      title={pricing.title}
      lead={pricing.lead}
      className="pricing-section"
    >
      <div className="pricing-billing">
        <div
          className="pricing-billing-toggle"
          role="group"
          aria-label="Ciclo de facturación"
        >
          <span
            className={`pricing-billing-toggle__slider${isAnnual ? ' pricing-billing-toggle__slider--annual' : ''}`}
            aria-hidden="true"
          />
          <button
            type="button"
            className={`pricing-billing-toggle__option${!isAnnual ? ' pricing-billing-toggle__option--active' : ''}`}
            aria-pressed={!isAnnual}
            onClick={() => setBilling('monthly')}
          >
            {pricing.billingToggle.monthly}
          </button>
          <button
            type="button"
            className={`pricing-billing-toggle__option${isAnnual ? ' pricing-billing-toggle__option--active' : ''}`}
            aria-pressed={isAnnual}
            onClick={() => setBilling('annual')}
          >
            {pricing.billingToggle.annual}
          </button>
        </div>
        <span className="pricing-billing-toggle__badge">{pricing.billingToggle.annualBadge} OFF</span>
        <p className="pricing-billing-hint">
          {isAnnual ? (
            <>
              Precio mensual equivalente con{' '}
              <strong>{PRICING_ANNUAL_DISCOUNT_PERCENT}% de descuento</strong> al pagar el año por
              adelantado
            </>
          ) : (
            pricing.billingToggle.annualHint
          )}
        </p>
      </div>

      <div className="pricing-grid">
        {pricing.plans.map((plan, i) => (
          <ScrollReveal key={plan.id} delay={i * 80}>
            <PricingCard plan={plan} isAnnual={isAnnual} />
          </ScrollReveal>
        ))}
      </div>

      {pricing.footnote ? <p className="pricing-footnote">{pricing.footnote}</p> : null}
    </Section>
  );
}
